import React from "react";
import {
  Stepper,
  Step,
  StepLabel,
  StepConnector,
  stepConnectorClasses,
  styled,
  Box,
  useTheme,
} from "@mui/material";
import { useSelector } from "react-redux";
import { RootState } from "../../../../store/Index";
import { tokens } from "../../../../theme";

type StepItem = string;

interface EmailTemplateStepperProps {
  steps: StepItem[];
  activeStep: number;
  onStepClick: (index: number) => void;
}
interface StyledConnectorProps {
  linecolor?: string;
  activelinecolor?: string;
}

const CustomConnector = styled(StepConnector)<StyledConnectorProps>(
  ({ linecolor, activelinecolor }) => ({
    [`&.${stepConnectorClasses.alternativeLabel}`]: {
      top: 15,
    },
    [`&.${stepConnectorClasses.active} .${stepConnectorClasses.line}`]: {
      backgroundColor: activelinecolor,
    },
    [`&.${stepConnectorClasses.completed} .${stepConnectorClasses.line}`]: {
      backgroundColor: activelinecolor,
    },
    [`& .${stepConnectorClasses.line}`]: {
      height: 2,
      border: 0,
      backgroundColor: linecolor,
      borderRadius: 1,
    },
  }),
);

interface StepIconCompProps {
  active?: boolean;
  completed?: boolean;
  icon: React.ReactNode;
  iconColor?: string;
  borderColor?: string;
  mutedTextColor?: string;
}

function StepIconComp({
  active,
  completed,
  icon,
  iconColor,
  borderColor,
  mutedTextColor,
}: StepIconCompProps) {
  return (
    <Box
      sx={{
        width: 32,
        height: 32,
        borderRadius: "50%",
        backgroundColor: completed || active ? "#fff" : "transparent",
        border: completed || active ? "none" : `2px solid ${borderColor}`,
        color: completed || active ? iconColor : mutedTextColor,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontWeight: 600,
        fontSize: 14,
        transition: "all 0.2s ease-in-out",
      }}
    >
      {icon}
    </Box>
  );
}

const EmailTemplateStepper: React.FC<EmailTemplateStepperProps> = ({
  steps,
  activeStep,
  onStepClick,
}) => {
  const theme = useTheme();
  const themeState = useSelector((state: RootState) => state.theme);
  const colors = tokens(theme.palette.mode, themeState);

  const isDarkMode = theme.palette.mode === "dark";

  const activeLineColor = isDarkMode ? colors.primary[500] : "#fff";
  const defaultLineColor = isDarkMode
    ? `${colors.primary[300]}40`
    : "rgba(255, 255, 255, 0.25)";
  const customIconTextColor = colors.primary[400] || "#8B5A2B";
  const stepBorderColor = isDarkMode
    ? `${colors.primary[300]}60`
    : "rgba(255, 255, 255, 0.4)";
  const inactiveTextColor = isDarkMode
    ? colors.primary[300]
    : "rgba(255, 255, 255, 0.6)";

  return (
    <Box sx={{ width: "100%" }}>
      <Stepper
        alternativeLabel
        activeStep={activeStep}
        connector={
          <CustomConnector
            activelinecolor={activeLineColor}
            linecolor={defaultLineColor}
          />
        }
      >
        {steps.map((label, index) => (
          <Step
            key={index}
            onClick={() => onStepClick(index)}
            sx={{ cursor: "pointer" }}
          >
            <StepLabel
              StepIconComponent={(props) => (
                <StepIconComp
                  active={props.active}
                  completed={props.completed}
                  icon={props.icon}
                  iconColor={customIconTextColor}
                  borderColor={stepBorderColor}
                  mutedTextColor={inactiveTextColor}
                />
              )}
              sx={{
                "& .MuiStepLabel-label": {
                  color: isDarkMode
                    ? colors.primary[300]
                    : "rgba(255, 255, 255, 0.4)",
                  mt: 1,
                  fontSize: "13px",
                  fontWeight: 500,
                  textTransform: "capitalize",
                  transition: "color 0.2s ease",
                  "&.Mui-active": {
                    color: isDarkMode ? colors.primary[700] : "#fff",
                    fontWeight: 600,
                  },
                  "&.Mui-completed": {
                    color: isDarkMode
                      ? colors.primary[100]
                      : "rgba(255, 255, 255, 0.7)",
                  },
                },
              }}
            >
              {label}
            </StepLabel>
          </Step>
        ))}
      </Stepper>
    </Box>
  );
};

export default EmailTemplateStepper;
